import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { authAxios as ax } from "@/contexts/AuthContext";
import Sidebar from "@/components/Sidebar";
import DndOperatorBoard from "@/components/DndOperatorBoard";
import NotificationBell from "@/components/NotificationBell";
import ProjectsTab from "@/components/admin/ProjectsTab";
import ScriptsTab from "@/components/admin/ScriptsTab";
import UsersTab from "@/components/admin/UsersTab";
import KnowledgeBaseTab from "@/components/admin/KnowledgeBaseTab";
import IntentHeatmap from "@/components/analytics/IntentHeatmap";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Phone, Clock, TrendingUp, Wifi, WifiOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-5 flex items-start gap-4" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: accent || "#002FA7" }}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-black text-[#0A0A0A] tracking-tight">{value}</p>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF] font-bold mt-0.5">{label}</p>
      </div>
    </div>
  );
}

export default function AdminMatrix() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [operators, setOperators] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [users, setUsers] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const wsRef = useRef(null);

  // WebSocket for live status
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backendUrl.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/admin-status?token=`);
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.projects) setProjects(msg.projects);
        if (msg.operators) setOperators(msg.operators);
        if (msg.online_count !== undefined) setOnlineCount(msg.online_count);
      } catch {}
    };
    wsRef.current = ws;
    return () => ws.close();
  }, []);

  const load = async () => {
    try {
      const [p, s, o, sc] = await Promise.all([
        ax.get(`${API}/admin/projects`),
        ax.get(`${API}/admin/stats`),
        ax.get(`${API}/admin/operators`),
        ax.get(`${API}/admin/scripts`),
      ]);
      setProjects(p.data);
      setStats(s.data);
      setOperators(o.data);
      setScripts(sc.data);
    } catch {}
    if (user?.role === "super-admin" || user?.role === "admin") {
      try { const u = await ax.get(`${API}/auth/users`); setUsers(u.data); } catch {}
    }
  };

  useEffect(() => { load(); }, []);

  const dndAssign = async (projectId, operatorId) => {
    try {
      await ax.post(`${API}/admin/projects/${projectId}/assign`, { operator_id: operatorId });
      load();
      wsRef.current?.send("refresh");
    } catch {}
  };
  const dndUnassign = async (projectId, operatorId) => {
    try {
      await ax.post(`${API}/admin/projects/${projectId}/unassign`, { operator_id: operatorId });
      load();
      wsRef.current?.send("refresh");
    } catch {}
  };

  const isAdminOrSuper = user?.role === "super-admin" || user?.role === "admin";

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight" data-testid="admin-title">
              {t("admin.title")}
            </h2>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <div className="flex items-center gap-1.5" data-testid="ws-status">
                {wsConnected ? <Wifi size={14} className="text-[#00C853]" /> : <WifiOff size={14} className="text-[#FF2A2A]" />}
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#9CA3AF]">
                  {wsConnected ? t("dnd.live_status") : "Offline"}
                </span>
              </div>
              <Badge variant="outline" className="rounded-none text-[10px] font-bold" data-testid="online-count">
                {onlineCount} {t("dnd.online")}
              </Badge>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={TrendingUp} label={t("admin.total_projects")} value={stats.total_projects || 0} accent="#002FA7" />
            <StatCard icon={Users} label={t("admin.total_operators")} value={stats.total_operators || 0} accent="#0A0A0A" />
            <StatCard icon={Phone} label={t("admin.total_calls")} value={stats.total_calls_today || 0} accent="#00C853" />
            <StatCard icon={Clock} label={t("admin.avg_satisfaction")} value={stats.avg_satisfaction || 0} accent="#FFB800" />
          </div>

          <Tabs defaultValue="projects" className="space-y-4">
            <TabsList className="bg-white border border-[#E5E7EB] rounded-none h-10 p-0.5 flex-wrap">
              <TabsTrigger value="projects" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-projects">{t("nav.projects")}</TabsTrigger>
              <TabsTrigger value="board" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-board">{t("dnd.resource_board")}</TabsTrigger>
              <TabsTrigger value="scripts" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-scripts">{t("nav.scripts")}</TabsTrigger>
              <TabsTrigger value="kb" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-kb">Knowledge Base</TabsTrigger>
              <TabsTrigger value="insights" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-insights">Insights</TabsTrigger>
              {isAdminOrSuper && (
                <TabsTrigger value="users" className="rounded-none text-xs font-bold tracking-[0.1em] uppercase data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white" data-testid="tab-users">{t("nav.users")}</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="projects">
              <ProjectsTab t={t} projects={projects} operators={operators} reload={load} user={user} />
            </TabsContent>

            <TabsContent value="board" className="space-y-4">
              <DndOperatorBoard projects={projects} operators={operators} onAssign={dndAssign} onUnassign={dndUnassign} t={t} />
            </TabsContent>

            <TabsContent value="scripts">
              <ScriptsTab t={t} scripts={scripts} reload={load} />
            </TabsContent>

            <TabsContent value="kb">
              <KnowledgeBaseTab t={t} />
            </TabsContent>

            <TabsContent value="insights">
              <IntentHeatmap t={t} />
            </TabsContent>

            {isAdminOrSuper && (
              <TabsContent value="users">
                <UsersTab t={t} users={users} projects={projects} reload={load} currentUser={user} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </main>
    </div>
  );
}
